/**
 * Share-landing attribution report — the receiving half of the share loop. The app deliberately
 * does not call `POST /share/attribution` (its links open THIS site first), so the web landing
 * page is the one place the report can happen. Parses the landing URL's `l_data` + `utm_*`
 * (minted by loopit-app/lib/shareTracker.ts or web/lib/shareLinks.ts) and reports once.
 */

import { hive } from "./apiClient";
import { parseLandingAttribution } from "./analytics";

/** One report per landing: the backend dedups by `said` for 24h, this guards the SPA session. */
const REPORTED_KEY = "muhaha.shareLanding.reported.v1";

// The path shapes a shared link can land on (router/index.tsx + AASA claims). The room link is
// absent on purpose: roomShareUrl carries no l_data, so there is nothing to report for it.
const LANDING_RE = /^\/(?:open\/)?(game|drama|invite|play)\/([^/?#]+)/;

interface ShareLanding {
	shareId: string;
	kind: "game" | "drama" | "invite";
	channel: string;
	ssuid: string;
	said: string | null;
	sharerId: string | null;
	utmSource: string | null;
	utmMedium: string | null;
	utmCampaign: string | null;
}

/**
 * The report for one landing URL, or null when this is not an attributable share landing
 * (path is not a share target, or the link carries no `l_data` session to attribute).
 */
export function parseShareLanding(pathname: string, search: string): ShareLanding | null {
	const match = LANDING_RE.exec(pathname);
	if (!match) return null;

	const { properties } = parseLandingAttribution(search);
	const ssuid = properties.share_ssuid;
	if (!ssuid) return null;

	return {
		shareId: decodeURIComponent(match[2]),
		kind: match[1] === "play" ? "game" : (match[1] as ShareLanding["kind"]),
		channel: properties.utm_medium ?? "unknown",
		ssuid,
		said: properties.share_said ?? null,
		sharerId: properties.sharer ?? null,
		utmSource: properties.utm_source ?? null,
		utmMedium: properties.utm_medium ?? null,
		utmCampaign: properties.utm_campaign ?? null,
	};
}

/**
 * Report the landing to Hive, best-effort: attribution must never break the page. Call once at
 * boot, before the router redirects `/open/*` away (same timing constraint as referralCapture).
 */
export function reportShareLanding(
	pathname: string = window.location.pathname,
	search: string = window.location.search,
): void {
	const landing = parseShareLanding(pathname, search);
	if (!landing) return;

	const dedupKey = landing.said ?? landing.ssuid;
	try {
		if (window.sessionStorage.getItem(REPORTED_KEY) === dedupKey) return;
		window.sessionStorage.setItem(REPORTED_KEY, dedupKey);
	} catch {
		// Storage blocked: report anyway, the backend's 24h said-dedup absorbs repeats.
	}

	void hive.share.recordLanding(landing).catch(() => {});
}
